import type { Priority } from "../tasks/types.js";
import type { RoleName } from "./roles.js";

/** Material scheme styles we expose (subset of `material-color-utilities`'
 *  `Variant`). Changes how chroma/hue are derived from the source color. */
export type SchemeVariant = "tonal_spot" | "vibrant" | "expressive" | "content" | "neutral";

export const SCHEME_VARIANTS: readonly SchemeVariant[] = [
  "tonal_spot", "vibrant", "expressive", "content", "neutral",
];

export type ThemeMode = "light" | "dark";

/** Provenance of a color picked by pixel-sampling a decorative palette board:
 *  which board and where. `hex` is the color sampled there — a board marker is
 *  only rendered while the target still resolves to that exact color. */
export interface BoardSample {
  board: string;
  x: number;
  y: number;
  hex: string;
}

/** Per-scope ENTITY color overrides (the "how specific you want to get" axis).
 *  Each value is a `#rrggbb`; absent ⇒ inherit the next-broader scope, finally
 *  the categorical palette. Topic/calendar maps are keyed by entity id. */
export interface EntityColorOverrides {
  /** All entities. */
  global?: string;
  /** By kind. */
  kind?: { task?: string; event?: string };
  /** By priority (drives the priority flag + accent). */
  priority?: Partial<Record<Priority, string>>;
  calendars?: Record<string, string>;
  topics?: Record<string, string>;
  /** Palette-board sample positions, keyed by board target key ("topic:<id>",
   *  "slot:<i>", "role:<mode>:<role>", …). Nested here so it persists/syncs/
   *  backs up with the entity JSON and resets with it on preset switch. */
  boardSamples?: Record<string, BoardSample>;
}

/** Per-mode overrides of individual GENERATED chrome roles (the "drill into a
 *  generated value" axis). Absent ⇒ use the generated role color. */
export interface RoleOverrides {
  light: Partial<Record<RoleName, string>>;
  dark: Partial<Record<RoleName, string>>;
}

/**
 * The active theme — a sync-ready record (structurally a `SyncableRecord`).
 * One per user today; the storage/sync layer supports N (future saved themes).
 */
export interface ThemeConfig {
  id: string;
  /** Preset this derives from (for "revert to default"), or "custom". */
  preset: string;
  variant: SchemeVariant;
  /** Chrome seed color (`#rrggbb`). Generates BOTH light + dark schemes. */
  source: string;
  /** Optional independent dark seed (per-mode tuning); null ⇒ derive from `source`. */
  sourceDark: string | null;
  /** Per-mode overrides of generated chrome roles. */
  roleOverrides: RoleOverrides;
  /** The categorical ENTITY palette (distinct hues; see `palette.ts`). */
  entityPalette: string[];
  /** Per-slot overrides of the categorical palette, keyed by slot index. */
  paletteOverrides: Record<number, string>;
  /** Per-scope entity color overrides. */
  entity: EntityColorOverrides;
  /** Wash knobs (folded in from the old `blockTintDiff`/`blockGradient` settings). */
  blockTintDiff: number;
  blockGradient: boolean;
  /** Saturation lift applied to the WHOLE entity palette (HCT chroma delta).
   *  0 = the curated palette as-is; positive = more vivid chips. */
  chromaBoost: number;
  /** When true, calendars with no explicit color fall back to a distinct
   *  categorical color (so plain default-calendar tasks read with their
   *  calendar's color). Opt-in; default on. */
  autoColorCalendars: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Default priority colors (traffic-light) when the theme has no override. */
export const DEFAULT_PRIORITY_COLORS: Record<Priority, string> = {
  none: "",
  low: "#22c55e",
  medium: "#eab308",
  high: "#ef4444",
};

/** Default fill for an event with no other color. */
export const DEFAULT_EVENT_COLOR = "#64748b"; // slate-500

export const ENTITY_PALETTE_SIZE = 12;

/** Default + clamp bounds for the vibrancy (chroma boost) slider. */
export const DEFAULT_CHROMA_BOOST = 0;
export const CHROMA_BOOST_MIN = -24;
export const CHROMA_BOOST_MAX = 28;
